// Mock data for Matrix Film Review - will be replaced with TMDB API

export const translations = {
  en: {
    nav: {
      home: 'Home',
      reviews: 'Movies',
      series: 'TV Series',
      editorial: 'Editorial',
      categories: 'Categories',
      about: 'About',
      stars: 'Stars'
    },
    common: {
      viewAll: 'View All',
      learnMore: 'Learn More',
      seeMore: 'See More'
    },
    hero: {
      title: 'Matrix Film Review',
      tagline: 'Beyond Ratings. Into Meaning.',
      description: 'Where films are decoded, analyzed, and understood beyond surface-level entertainment.'
    },
    sections: {
      featured: 'Featured Reviews',
      categories: 'Explore Categories',
      editorial: 'Editorial',
      signals: 'Matrix Signals',
      stars: 'Hollywood Stars'
    },
    stars: {
      title: 'Hollywood Stars',
      subtitle: 'Discover the talent behind the magic',
      description: 'Explore the actors, directors, and talent that bring cinema to life',
      viewAll: 'View All Stars',
      biography: 'Biography',
      recentMovies: 'Recent & Popular Movies',
      backToStars: 'Back to Stars',
      notFound: 'Star Not Found',
      page: 'Page',
      of: 'of',
      noResults: 'No stars found',
      search: 'Search',
      searchPlaceholder: 'Search for actors, directors...',
      searchingFor: 'Showing results for',
      clearSearch: 'Clear search'
    },
    series: {
      title: 'Trending TV Series',
      subtitle: 'Popular shows from streaming platforms',
      viewAll: 'View All',
      pageTitle: 'TV Series',
      pageDescription: 'Explore shows from Netflix, Disney+, HBO, and more',
      searchPlaceholder: 'Search TV series...',
      search: 'Search',
      popular: 'Popular',
      trending: 'Trending',
      topRated: 'Top Rated',
      onTheAir: 'On The Air',
      streamingServices: 'Streaming Services',
      filterByService: 'Filter by streaming service:',
      searchingFor: 'Showing results for',
      filteringBy: 'Filtering by',
      clearFilter: 'Clear filter',
      page: 'Page',
      of: 'of',
      noResults: 'No series found',
      notFound: 'Series Not Found',
      backToSeries: 'Back to TV Series',
      overview: 'Overview',
      status: 'Status',
      availableOn: 'Available on',
      createdBy: 'Created by',
      cast: 'Cast',
      seasons: 'Seasons',
      similarShows: 'Similar Shows'
    },
    categories: {
      newReleases: 'New Releases',
      classics: 'Classics Revisited',
      cult: 'Cult Films',
      documentaries: 'Documentaries',
      hiddenGems: 'Hidden Gems',
      directors: 'Director Spotlights'
    },
    signals: {
      narrativeDepth: 'Narrative Depth',
      emotionalImpact: 'Emotional Impact',
      visualIdentity: 'Visual Identity',
      originality: 'Originality',
      rewatchValue: 'Rewatch Value'
    },
    footer: {
      brand: 'Matrix Film Review',
      tagline: 'Part of the Movie Matrix Universe',
      rights: 'All rights reserved',
      links: {
        trailers: 'TheMovieMatrixTrailers',
        tv: 'TheMovieMatrixTV'
      }
    },
    review: {
      theExperience: 'The Experience',
      themesAndMeaning: 'Themes & Meaning',
      directionAndPerformances: 'Direction & Performances',
      visualLanguage: 'Visual Language',
      finalThoughts: 'Final Thoughts',
      similarFilms: 'Similar Films'
    },
    about: {
      ourMission: 'Our Mission',
      title: 'About Matrix Film Review',
      tagline: 'Beyond Ratings. Into Meaning.',
      intro1: 'Matrix Film Review is a cinematic review platform dedicated to exploring films beyond simple scores. We analyze storytelling, direction, themes, and cultural impact to uncover the deeper meaning behind each movie.',
      intro2: "In an age of algorithmic recommendations and numerical aggregates, we believe something essential gets lost. A film's worth cannot be reduced to a percentage or a star count. Cinema is art, and art demands contemplation.",
      intro3: "Our Matrix Signals evaluation system measures what matters: narrative depth, emotional resonance, visual identity, originality, and rewatch value. These aren't arbitrary metrics—they're the dimensions that separate forgettable content from lasting cinema.",
      intro4: 'This platform is part of the larger Movie Matrix universe, connecting trailers, reviews, and curated recommendations for true film lovers who seek more than surface-level entertainment.',
      universeTitle: 'The Movie Matrix Universe',
      trailersDesc: 'Curated trailers across genres',
      tvDesc: 'Deep dives on YouTube',
      communityTitle: 'Join Our Community',
      communityDesc: 'Community features including comments, favorites, and user ratings are coming soon.',
      comingSoon: 'Coming Soon',
      valuesTitle: 'What We Value',
      ctaTitle: 'Ready to explore cinema differently?',
      ctaDesc: 'Discover our in-depth reviews and experience film analysis that goes beyond the surface.',
      ctaButton: 'Explore Reviews',
      values: {
        deepAnalysis: 'Deep Analysis',
        deepAnalysisDesc: 'We explore narrative structure, thematic layers, and storytelling techniques that make films resonate.',
        visualLanguage: 'Visual Language',
        visualLanguageDesc: 'Cinematography, color theory, and visual composition are central to our understanding of cinema.',
        emotionalImpact: 'Emotional Impact',
        emotionalImpactDesc: 'We measure how films connect with audiences on an emotional and psychological level.',
        culturalContext: 'Cultural Context',
        culturalContextDesc: 'Every film exists within a cultural moment. We examine relevance, influence, and legacy.'
      }
    },
    editorial: {
      description: 'Essays, analyses, and explorations of cinema\'s deeper meanings. Writing for those who see film as more than entertainment.',
      readArticle: 'Read Article',
      backToEditorial: 'Back to Editorial',
      minRead: 'min read',
      articleNotFound: 'Article Not Found',
      viewAll: 'View All',
      articles: {
        1: {
          title: 'Films That Question Reality',
          excerpt: 'From The Matrix to Inception, exploring cinema\'s obsession with perception and truth.',
          category: 'Analysis',
          content: {
            intro: 'Cinema has always been a medium that challenges our perception of reality. From the earliest days of the Lumière brothers\' train arriving at a station — causing audiences to reportedly flee in terror — to the mind-bending narratives of modern science fiction, film has constantly pushed the boundaries of what we accept as "real."',
            para1: 'The films that question reality most effectively don\'t simply present alternative worlds; they force us to examine our assumptions about the world we inhabit. They create a space where the audience becomes an active participant in questioning their own existence.',
            heading1: 'The Philosophy of Perception',
            para2: 'What separates truly great reality-questioning cinema from mere gimmickry is philosophical depth. A twist ending that reveals "it was all a dream" is cheap; a narrative that makes us question whether our own experiences might be equally illusory is profound.',
            para3: 'These films tap into ancient philosophical questions — Plato\'s Cave, Descartes\' Evil Demon, the Buddhist concept of Maya — and translate them into visceral, emotionally engaging experiences. They make philosophy accessible without dumbing it down.',
            heading2: 'Technical Innovation',
            para4: 'The best films in this tradition don\'t just tell us that reality is uncertain; they show us through innovative cinematography, editing, and sound design. The camera becomes unreliable. Time becomes non-linear. Sound and image disconnect in unsettling ways.',
            para5: 'In an age where deep fakes and virtual reality are becoming increasingly sophisticated, these films feel more relevant than ever. They prepare us to question, to doubt, to examine critically — skills we desperately need in our information-saturated world.'
          }
        },
        2: {
          title: 'Movies That Age Better With Time',
          excerpt: 'Why some films grow more relevant and powerful decades after their release.',
          category: 'Retrospective',
          content: {
            intro: 'Some films arrive ahead of their time, misunderstood or underappreciated upon release, only to be recognized as masterpieces years or decades later. These are the films that seem to grow richer and more relevant with each passing year.',
            para1: 'What makes a film age well? It\'s not simply technical excellence, though that helps. It\'s the ability to tap into universal human experiences while also capturing something specific about a moment in time that later generations can recognize and relate to.',
            heading1: 'The Test of Time',
            para2: 'Consider Blade Runner, a commercial disappointment in 1982 that is now considered one of the greatest science fiction films ever made. Its themes of identity, memory, and what it means to be human resonate more powerfully in our age of AI than they did forty years ago.',
            para3: 'Similarly, films like The Shining, Fight Club, and 2001: A Space Odyssey were all initially met with mixed reactions, only to be reassessed and celebrated by later audiences who could see what the original critics missed.',
            heading2: 'Cultural Resonance',
            para4: 'The films that age best often deal with themes that transcend their immediate context: the nature of power, the search for meaning, the complexity of human relationships. They may be set in specific times and places, but their insights remain universal.',
            para5: 'As our world changes, these films change with us, revealing new layers of meaning that their creators may not have consciously intended. They become living documents, conversations across time between filmmakers and audiences.'
          }
        },
        3: {
          title: 'When Style Becomes the Story',
          excerpt: 'Directors who weaponize aesthetics to create meaning beyond dialogue.',
          category: 'Essay',
          content: {
            intro: 'In the hands of certain directors, visual style transcends mere decoration to become an essential element of storytelling. These filmmakers understand that how we see something fundamentally shapes what we understand about it.',
            para1: 'Consider the work of Wong Kar-wai, whose saturated colors, step-printed slow motion, and cramped compositions don\'t just look beautiful — they express the emotional states of characters trapped by time, memory, and desire.',
            heading1: 'Visual Language',
            para2: 'Wes Anderson\'s meticulous symmetry and dollhouse aesthetics aren\'t affectations; they create worlds where characters exist in carefully ordered spaces that contrast with their internal chaos. The style is the statement.',
            para3: 'Denis Villeneuve uses negative space and monumental scale to make characters feel small against the vastness of their challenges. David Lynch weaponizes the uncanny, making the familiar strange through lighting, sound design, and pacing.',
            heading2: 'Beyond Decoration',
            para4: 'These directors prove that film is not illustrated radio — not simply dialogue and plot captured on camera. The visual medium has its own vocabulary, grammar, and poetry. Style, in their hands, becomes substance.',
            para5: 'When we dismiss a film as "all style, no substance," we often miss the point. For these filmmakers, style IS substance. The aesthetic choices are arguments, the compositions are revelations, and the color palettes are emotional landscapes.'
          }
        }
      }
    },
    reviews: {
      description: 'Deep analyses of cinema\'s most compelling works. Each review deconstructs the experience, themes, and artistry that make these films worth your time.',
      searching: 'Searching...',
      found: 'Found',
      reviewsCount: 'reviews',
      forQuery: 'for',
      readReview: 'Read Review',
      noImage: 'No Image',
      noResults: 'No reviews match your criteria.',
      clearFilters: 'Clear filters',
      director: 'Dir.',
      backToReviews: 'Back to Reviews',
      reviewNotFound: 'Review Not Found',
      share: 'Share',
      filmDetails: 'Film Details',
      directorLabel: 'Director',
      yearLabel: 'Year',
      otherMovies: 'Other Movies on TMDB',
      noReviewYet: 'No review yet',
      runtimeLabel: 'Runtime',
      genreLabel: 'Genre',
      yourRating: 'Your Rating',
      signInToRate: 'Sign in to rate',
      clickToRate: 'Click to rate this film',
      youRated: 'You rated',
      ratings: 'ratings'
    }
  },
  es: {
    nav: {
      home: 'Inicio',
      reviews: 'Películas',
      series: 'Series de TV',
      editorial: 'Editorial',
      categories: 'Categorías',
      about: 'Acerca de',
      stars: 'Estrellas'
    },
    common: {
      viewAll: 'Ver Todo',
      learnMore: 'Saber Más',
      seeMore: 'Ver Más'
    },
    hero: {
      title: 'Matrix Film Review',
      tagline: 'Más Allá de las Puntuaciones. Hacia el Significado.',
      description: 'Donde las películas son decodificadas, analizadas y comprendidas más allá del entretenimiento superficial.'
    },
    sections: {
      featured: 'Reseñas Destacadas',
      categories: 'Explorar Categorías',
      editorial: 'Editorial',
      signals: 'Señales Matrix',
      stars: 'Estrellas de Hollywood'
    },
    stars: {
      title: 'Estrellas de Hollywood',
      subtitle: 'Descubre el talento detrás de la magia',
      description: 'Explora los actores, directores y talentos que dan vida al cine',
      viewAll: 'Ver Todas las Estrellas',
      biography: 'Biografía',
      recentMovies: 'Películas Recientes y Populares',
      backToStars: 'Volver a Estrellas',
      notFound: 'Estrella No Encontrada',
      page: 'Página',
      of: 'de',
      noResults: 'No se encontraron estrellas',
      search: 'Buscar',
      searchPlaceholder: 'Buscar actores, directores...',
      searchingFor: 'Mostrando resultados para',
      clearSearch: 'Limpiar búsqueda'
    },
    series: {
      title: 'Series de TV en Tendencia',
      subtitle: 'Shows populares de plataformas de streaming',
      viewAll: 'Ver Todo',
      pageTitle: 'Series de TV',
      pageDescription: 'Explora shows de Netflix, Disney+, HBO y más',
      searchPlaceholder: 'Buscar series de TV...',
      search: 'Buscar',
      popular: 'Popular',
      trending: 'Tendencias',
      topRated: 'Mejor Valoradas',
      onTheAir: 'En Emisión',
      streamingServices: 'Servicios de Streaming',
      filterByService: 'Filtrar por servicio de streaming:',
      searchingFor: 'Mostrando resultados para',
      filteringBy: 'Filtrando por',
      clearFilter: 'Limpiar filtro',
      page: 'Página',
      of: 'de',
      noResults: 'No se encontraron series',
      notFound: 'Serie No Encontrada',
      backToSeries: 'Volver a Series de TV',
      overview: 'Sinopsis',
      status: 'Estado',
      availableOn: 'Disponible en',
      createdBy: 'Creada por',
      cast: 'Reparto',
      seasons: 'Temporadas',
      similarShows: 'Shows Similares'
    },
    categories: {
      newReleases: 'Estrenos',
      classics: 'Clásicos Revisitados',
      cult: 'Películas de Culto',
      documentaries: 'Documentales',
      hiddenGems: 'Joyas Ocultas',
      directors: 'Directores Destacados'
    },
    signals: {
      narrativeDepth: 'Profundidad Narrativa',
      emotionalImpact: 'Impacto Emocional',
      visualIdentity: 'Identidad Visual',
      originality: 'Originalidad',
      rewatchValue: 'Valor de Repetición'
    },
    footer: {
      brand: 'Matrix Film Review',
      tagline: 'Parte del Universo Movie Matrix',
      rights: 'Todos los derechos reservados',
      links: {
        trailers: 'TheMovieMatrixTrailers',
        tv: 'TheMovieMatrixTV'
      }
    },
    review: {
      theExperience: 'La Experiencia',
      themesAndMeaning: 'Temas y Significado',
      directionAndPerformances: 'Dirección e Interpretaciones',
      visualLanguage: 'Lenguaje Visual',
      finalThoughts: 'Reflexiones Finales',
      similarFilms: 'Películas Similares'
    },
    about: {
      ourMission: 'Nuestra Misión',
      title: 'Acerca de Matrix Film Review',
      tagline: 'Más Allá de las Puntuaciones. Hacia el Significado.',
      intro1: 'Matrix Film Review es una plataforma de crítica cinematográfica dedicada a explorar películas más allá de simples puntuaciones. Analizamos narrativa, dirección, temas e impacto cultural para descubrir el significado profundo detrás de cada película.',
      intro2: 'En una era de recomendaciones algorítmicas y agregados numéricos, creemos que algo esencial se pierde. El valor de una película no puede reducirse a un porcentaje o una cantidad de estrellas. El cine es arte, y el arte exige contemplación.',
      intro3: 'Nuestro sistema de evaluación Matrix Signals mide lo que importa: profundidad narrativa, resonancia emocional, identidad visual, originalidad y valor de repetición. No son métricas arbitrarias, son las dimensiones que separan el contenido olvidable del cine perdurable.',
      intro4: 'Esta plataforma es parte del universo más amplio de Movie Matrix, conectando tráilers, reseñas y recomendaciones curadas para verdaderos amantes del cine que buscan más que entretenimiento superficial.',
      universeTitle: 'El Universo Movie Matrix',
      trailersDesc: 'Tráilers curados de todos los géneros',
      tvDesc: 'Análisis profundos en YouTube',
      communityTitle: 'Únete a Nuestra Comunidad',
      communityDesc: 'Las funciones de comunidad, incluyendo comentarios, favoritos y calificaciones de usuarios, estarán disponibles pronto.',
      comingSoon: 'Próximamente',
      valuesTitle: 'Lo Que Valoramos',
      ctaTitle: '¿Listo para explorar el cine de manera diferente?',
      ctaDesc: 'Descubre nuestras reseñas en profundidad y experimenta un análisis cinematográfico que va más allá de la superficie.',
      ctaButton: 'Explorar Reseñas',
      values: {
        deepAnalysis: 'Análisis Profundo',
        deepAnalysisDesc: 'Exploramos la estructura narrativa, las capas temáticas y las técnicas de narración que hacen que las películas resuenen.',
        visualLanguage: 'Lenguaje Visual',
        visualLanguageDesc: 'La cinematografía, la teoría del color y la composición visual son centrales para nuestra comprensión del cine.',
        emotionalImpact: 'Impacto Emocional',
        emotionalImpactDesc: 'Medimos cómo las películas conectan con las audiencias a nivel emocional y psicológico.',
        culturalContext: 'Contexto Cultural',
        culturalContextDesc: 'Cada película existe dentro de un momento cultural. Examinamos relevancia, influencia y legado.'
      }
    },
    editorial: {
      description: 'Ensayos, análisis y exploraciones de los significados más profundos del cine. Escritos para quienes ven el cine como algo más que entretenimiento.',
      readArticle: 'Leer Artículo',
      backToEditorial: 'Volver a Editorial',
      minRead: 'min de lectura',
      articleNotFound: 'Artículo No Encontrado',
      viewAll: 'Ver Todo',
      articles: {
        1: {
          title: 'Películas Que Cuestionan la Realidad',
          excerpt: 'De The Matrix a Inception, explorando la obsesión del cine con la percepción y la verdad.',
          category: 'Análisis',
          content: {
            intro: 'El cine siempre ha sido un medio que desafía nuestra percepción de la realidad. Desde los primeros días del tren de los hermanos Lumière llegando a la estación — causando que el público supuestamente huyera aterrorizado — hasta las narrativas desconcertantes de la ciencia ficción moderna, el cine ha empujado constantemente los límites de lo que aceptamos como "real".',
            para1: 'Las películas que cuestionan la realidad de manera más efectiva no simplemente presentan mundos alternativos; nos obligan a examinar nuestras suposiciones sobre el mundo que habitamos. Crean un espacio donde el espectador se convierte en un participante activo en cuestionar su propia existencia.',
            heading1: 'La Filosofía de la Percepción',
            para2: 'Lo que separa al cine verdaderamente grande que cuestiona la realidad del mero truco es la profundidad filosófica. Un final sorpresa que revela "todo fue un sueño" es barato; una narrativa que nos hace cuestionar si nuestras propias experiencias podrían ser igualmente ilusorias es profunda.',
            para3: 'Estas películas tocan preguntas filosóficas antiguas — la Caverna de Platón, el Demonio Maligno de Descartes, el concepto budista de Maya — y las traducen en experiencias viscerales y emocionalmente cautivadoras. Hacen la filosofía accesible sin simplificarla.',
            heading2: 'Innovación Técnica',
            para4: 'Las mejores películas de esta tradición no solo nos dicen que la realidad es incierta; nos lo muestran a través de cinematografía innovadora, edición y diseño de sonido. La cámara se vuelve poco confiable. El tiempo se vuelve no lineal. El sonido y la imagen se desconectan de maneras inquietantes.',
            para5: 'En una era donde los deepfakes y la realidad virtual se vuelven cada vez más sofisticados, estas películas se sienten más relevantes que nunca. Nos preparan para cuestionar, dudar, examinar críticamente — habilidades que necesitamos desesperadamente en nuestro mundo saturado de información.'
          }
        },
        2: {
          title: 'Películas Que Mejoran Con el Tiempo',
          excerpt: 'Por qué algunas películas se vuelven más relevantes y poderosas décadas después de su estreno.',
          category: 'Retrospectiva',
          content: {
            intro: 'Algunas películas llegan antes de su tiempo, incomprendidas o subestimadas en su estreno, solo para ser reconocidas como obras maestras años o décadas después. Estas son las películas que parecen enriquecerse y volverse más relevantes con cada año que pasa.',
            para1: '¿Qué hace que una película envejezca bien? No es simplemente la excelencia técnica, aunque eso ayuda. Es la capacidad de conectar con experiencias humanas universales mientras también captura algo específico sobre un momento en el tiempo que generaciones posteriores pueden reconocer y relacionar.',
            heading1: 'La Prueba del Tiempo',
            para2: 'Considera Blade Runner, un fracaso comercial en 1982 que ahora es considerada una de las mejores películas de ciencia ficción jamás realizadas. Sus temas de identidad, memoria y lo que significa ser humano resuenan más poderosamente en nuestra era de IA que hace cuarenta años.',
            para3: 'De manera similar, películas como El Resplandor, Fight Club y 2001: Una Odisea del Espacio fueron inicialmente recibidas con reacciones mixtas, solo para ser reevaluadas y celebradas por audiencias posteriores que pudieron ver lo que los críticos originales no vieron.',
            heading2: 'Resonancia Cultural',
            para4: 'Las películas que mejor envejecen a menudo tratan temas que trascienden su contexto inmediato: la naturaleza del poder, la búsqueda de significado, la complejidad de las relaciones humanas. Pueden estar ambientadas en tiempos y lugares específicos, pero sus percepciones permanecen universales.',
            para5: 'A medida que nuestro mundo cambia, estas películas cambian con nosotros, revelando nuevas capas de significado que sus creadores pueden no haber pretendido conscientemente. Se convierten en documentos vivos, conversaciones a través del tiempo entre cineastas y audiencias.'
          }
        },
        3: {
          title: 'Cuando el Estilo Se Convierte en la Historia',
          excerpt: 'Directores que utilizan la estética como arma para crear significado más allá del diálogo.',
          category: 'Ensayo',
          content: {
            intro: 'En manos de ciertos directores, el estilo visual trasciende la mera decoración para convertirse en un elemento esencial de la narración. Estos cineastas entienden que cómo vemos algo moldea fundamentalmente lo que entendemos sobre ello.',
            para1: 'Considera el trabajo de Wong Kar-wai, cuyos colores saturados, cámara lenta y composiciones estrechas no solo se ven hermosos — expresan los estados emocionales de personajes atrapados por el tiempo, la memoria y el deseo.',
            heading1: 'Lenguaje Visual',
            para2: 'La simetría meticulosa y la estética de casa de muñecas de Wes Anderson no son afectaciones; crean mundos donde los personajes existen en espacios cuidadosamente ordenados que contrastan con su caos interno. El estilo es la declaración.',
            para3: 'Denis Villeneuve usa el espacio negativo y la escala monumental para hacer que los personajes se sientan pequeños contra la vastedad de sus desafíos. David Lynch utiliza lo inquietante, haciendo extraño lo familiar a través de la iluminación, el diseño de sonido y el ritmo.',
            heading2: 'Más Allá de la Decoración',
            para4: 'Estos directores demuestran que el cine no es radio ilustrada — no simplemente diálogo y trama capturados en cámara. El medio visual tiene su propio vocabulario, gramática y poesía. El estilo, en sus manos, se convierte en sustancia.',
            para5: 'Cuando descartamos una película como "todo estilo, sin sustancia", a menudo perdemos el punto. Para estos cineastas, el estilo ES sustancia. Las elecciones estéticas son argumentos, las composiciones son revelaciones, y las paletas de color son paisajes emocionales.'
          }
        }
      }
    },
    reviews: {
      description: 'Análisis profundos de las obras más cautivadoras del cine. Cada reseña deconstruye la experiencia, los temas y el arte que hacen que estas películas valgan tu tiempo.',
      searching: 'Buscando...',
      found: 'Encontradas',
      reviewsCount: 'reseñas',
      forQuery: 'para',
      readReview: 'Leer Reseña',
      noImage: 'Sin Imagen',
      noResults: 'Ninguna reseña coincide con tus criterios.',
      clearFilters: 'Limpiar filtros',
      director: 'Dir.',
      backToReviews: 'Volver a Reseñas',
      reviewNotFound: 'Reseña No Encontrada',
      share: 'Compartir',
      filmDetails: 'Detalles de la Película',
      directorLabel: 'Director',
      yearLabel: 'Año',
      runtimeLabel: 'Duración',
      genreLabel: 'Género',
      yourRating: 'Tu Calificación',
      signInToRate: 'Inicia sesión para calificar',
      clickToRate: 'Haz clic para calificar esta película',
      youRated: 'Calificaste',
      ratings: 'calificaciones'
    }
  },
  pt: {
    nav: {
      home: 'Início',
      reviews: 'Filmes',
      series: 'Séries de TV',
      editorial: 'Editorial',
      categories: 'Categorias',
      about: 'Sobre',
      stars: 'Estrelas'
    },
    common: {
      viewAll: 'Ver Tudo',
      learnMore: 'Saiba Mais',
      seeMore: 'Ver Mais'
    },
    hero: {
      title: 'Matrix Film Review',
      tagline: 'Além das Notas. No Significado.',
      description: 'Onde os filmes são decodificados, analisados e compreendidos além do entretenimento superficial.'
    },
    sections: {
      featured: 'Críticas em Destaque',
      categories: 'Explorar Categorias',
      editorial: 'Editorial',
      signals: 'Sinais Matrix',
      stars: 'Estrelas de Hollywood'
    },
    stars: {
      title: 'Estrelas de Hollywood',
      subtitle: 'Descubra o talento por trás da magia',
      description: 'Explore os atores, diretores e talentos que dão vida ao cinema',
      viewAll: 'Ver Todas as Estrelas',
      biography: 'Biografia',
      recentMovies: 'Filmes Recentes e Populares',
      backToStars: 'Voltar às Estrelas',
      notFound: 'Estrela Não Encontrada',
      page: 'Página',
      of: 'de',
      noResults: 'Nenhuma estrela encontrada',
      search: 'Buscar',
      searchPlaceholder: 'Buscar atores, diretores...',
      searchingFor: 'Mostrando resultados para',
      clearSearch: 'Limpar busca'
    },
    series: {
      title: 'Séries de TV em Tendência',
      subtitle: 'Shows populares das plataformas de streaming',
      viewAll: 'Ver Tudo',
      pageTitle: 'Séries de TV',
      pageDescription: 'Explore shows da Netflix, Disney+, HBO e mais',
      searchPlaceholder: 'Buscar séries de TV...',
      search: 'Buscar',
      popular: 'Popular',
      trending: 'Em Alta',
      topRated: 'Mais Bem Avaliadas',
      onTheAir: 'No Ar',
      streamingServices: 'Serviços de Streaming',
      filterByService: 'Filtrar por serviço de streaming:',
      searchingFor: 'Mostrando resultados para',
      filteringBy: 'Filtrando por',
      clearFilter: 'Limpar filtro',
      page: 'Página',
      of: 'de',
      noResults: 'Nenhuma série encontrada',
      notFound: 'Série Não Encontrada',
      backToSeries: 'Voltar às Séries de TV',
      overview: 'Sinopse',
      status: 'Status',
      availableOn: 'Disponível em',
      createdBy: 'Criada por',
      cast: 'Elenco',
      seasons: 'Temporadas',
      similarShows: 'Shows Similares'
    },
    categories: {
      newReleases: 'Lançamentos',
      classics: 'Clássicos Revisitados',
      cult: 'Filmes Cult',
      documentaries: 'Documentários',
      hiddenGems: 'Joias Escondidas',
      directors: 'Destaque de Diretores'
    },
    signals: {
      narrativeDepth: 'Profundidade Narrativa',
      emotionalImpact: 'Impacto Emocional',
      visualIdentity: 'Identidade Visual',
      originality: 'Originalidade',
      rewatchValue: 'Valor de Revisão'
    },
    footer: {
      brand: 'Matrix Film Review',
      tagline: 'Parte do Universo Movie Matrix',
      rights: 'Todos os direitos reservados',
      links: {
        trailers: 'TheMovieMatrixTrailers',
        tv: 'TheMovieMatrixTV'
      }
    },
    review: {
      theExperience: 'A Experiência',
      themesAndMeaning: 'Temas e Significado',
      directionAndPerformances: 'Direção e Atuações',
      visualLanguage: 'Linguagem Visual',
      finalThoughts: 'Considerações Finais',
      similarFilms: 'Filmes Similares'
    },
    about: {
      ourMission: 'Nossa Missão',
      title: 'Sobre Matrix Film Review',
      tagline: 'Além das Notas. No Significado.',
      intro1: 'Matrix Film Review é uma plataforma de crítica cinematográfica dedicada a explorar filmes além de simples pontuações. Analisamos narrativa, direção, temas e impacto cultural para descobrir o significado profundo por trás de cada filme.',
      intro2: 'Em uma era de recomendações algorítmicas e agregados numéricos, acreditamos que algo essencial se perde. O valor de um filme não pode ser reduzido a uma porcentagem ou quantidade de estrelas. O cinema é arte, e a arte exige contemplação.',
      intro3: 'Nosso sistema de avaliação Matrix Signals mede o que importa: profundidade narrativa, ressonância emocional, identidade visual, originalidade e valor de revisão.',
      intro4: 'Esta plataforma é parte do universo maior do Movie Matrix, conectando trailers, críticas e recomendações curadas para verdadeiros amantes do cinema.',
      universeTitle: 'O Universo Movie Matrix',
      trailersDesc: 'Trailers curados de todos os gêneros',
      tvDesc: 'Análises profundas no YouTube',
      communityTitle: 'Junte-se à Nossa Comunidade',
      communityDesc: 'Recursos da comunidade, incluindo comentários, favoritos e avaliações de usuários, estarão disponíveis em breve.',
      comingSoon: 'Em Breve',
      valuesTitle: 'O Que Valorizamos',
      ctaTitle: 'Pronto para explorar o cinema de forma diferente?',
      ctaDesc: 'Descubra nossas críticas detalhadas e experimente análises cinematográficas que vão além da superfície.',
      ctaButton: 'Explorar Críticas',
      values: {
        deepAnalysis: 'Análise Profunda',
        deepAnalysisDesc: 'Exploramos estrutura narrativa, camadas temáticas e técnicas de narrativa que fazem os filmes ressoarem.',
        visualLanguage: 'Linguagem Visual',
        visualLanguageDesc: 'Cinematografia, teoria das cores e composição visual são centrais para nossa compreensão do cinema.',
        emotionalImpact: 'Impacto Emocional',
        emotionalImpactDesc: 'Medimos como os filmes conectam com as audiências em nível emocional e psicológico.',
        culturalContext: 'Contexto Cultural',
        culturalContextDesc: 'Cada filme existe dentro de um momento cultural. Examinamos relevância, influência e legado.'
      }
    },
    editorial: {
      description: 'Ensaios, análises e explorações dos significados mais profundos do cinema. Escritos para quem vê o cinema como mais do que entretenimento.',
      readArticle: 'Ler Artigo',
      backToEditorial: 'Voltar ao Editorial',
      minRead: 'min de leitura',
      articleNotFound: 'Artigo Não Encontrado',
      viewAll: 'Ver Tudo',
      articles: {
        1: {
          title: 'Filmes Que Questionam a Realidade',
          excerpt: 'De The Matrix a Inception, explorando a obsessão do cinema com percepção e verdade.',
          category: 'Análise',
          content: {
            intro: 'O cinema sempre foi um meio que desafia nossa percepção da realidade. Desde os primeiros dias do trem dos irmãos Lumière chegando à estação — fazendo com que o público supostamente fugisse aterrorizado — até as narrativas desconcertantes da ficção científica moderna, o cinema constantemente empurrou os limites do que aceitamos como "real".',
            para1: 'Os filmes que questionam a realidade de forma mais eficaz não simplesmente apresentam mundos alternativos; eles nos forçam a examinar nossas suposições sobre o mundo que habitamos. Eles criam um espaço onde o público se torna um participante ativo em questionar sua própria existência.',
            heading1: 'A Filosofia da Percepção',
            para2: 'O que separa o cinema verdadeiramente grande que questiona a realidade do mero truque é a profundidade filosófica. Um final surpresa que revela "tudo era um sonho" é barato; uma narrativa que nos faz questionar se nossas próprias experiências podem ser igualmente ilusórias é profunda.',
            para3: 'Esses filmes tocam em questões filosóficas antigas — a Caverna de Platão, o Gênio Maligno de Descartes, o conceito budista de Maya — e as traduzem em experiências viscerais e emocionalmente envolventes.',
            heading2: 'Inovação Técnica',
            para4: 'Os melhores filmes desta tradição não apenas nos dizem que a realidade é incerta; eles nos mostram através de cinematografia inovadora, edição e design de som. A câmera se torna não confiável. O tempo se torna não linear.',
            para5: 'Em uma era onde deepfakes e realidade virtual estão se tornando cada vez mais sofisticados, esses filmes parecem mais relevantes do que nunca. Eles nos preparam para questionar, duvidar, examinar criticamente.'
          }
        },
        2: {
          title: 'Filmes Que Melhoram Com o Tempo',
          excerpt: 'Por que alguns filmes se tornam mais relevantes e poderosos décadas após seu lançamento.',
          category: 'Retrospectiva',
          content: {
            intro: 'Alguns filmes chegam antes de seu tempo, incompreendidos ou subestimados em seu lançamento, apenas para serem reconhecidos como obras-primas anos ou décadas depois.',
            para1: 'O que faz um filme envelhecer bem? Não é simplesmente excelência técnica, embora isso ajude. É a capacidade de tocar experiências humanas universais enquanto também captura algo específico sobre um momento no tempo.',
            heading1: 'O Teste do Tempo',
            para2: 'Considere Blade Runner, um fracasso comercial em 1982 que agora é considerado um dos maiores filmes de ficção científica já feitos. Seus temas de identidade, memória e o que significa ser humano ressoam mais poderosamente em nossa era de IA.',
            para3: 'Da mesma forma, filmes como O Iluminado, Clube da Luta e 2001: Uma Odisseia no Espaço foram inicialmente recebidos com reações mistas.',
            heading2: 'Ressonância Cultural',
            para4: 'Os filmes que melhor envelhecem frequentemente lidam com temas que transcendem seu contexto imediato: a natureza do poder, a busca por significado, a complexidade das relações humanas.',
            para5: 'À medida que nosso mundo muda, esses filmes mudam conosco, revelando novas camadas de significado que seus criadores podem não ter pretendido conscientemente.'
          }
        },
        3: {
          title: 'Quando o Estilo Se Torna a História',
          excerpt: 'Diretores que usam a estética como arma para criar significado além do diálogo.',
          category: 'Ensaio',
          content: {
            intro: 'Nas mãos de certos diretores, o estilo visual transcende a mera decoração para se tornar um elemento essencial da narrativa.',
            para1: 'Considere o trabalho de Wong Kar-wai, cujas cores saturadas, câmera lenta e composições apertadas não apenas parecem bonitas — elas expressam os estados emocionais de personagens presos pelo tempo, memória e desejo.',
            heading1: 'Linguagem Visual',
            para2: 'A simetria meticulosa e a estética de casa de bonecas de Wes Anderson não são afetações; elas criam mundos onde os personagens existem em espaços cuidadosamente ordenados.',
            para3: 'Denis Villeneuve usa espaço negativo e escala monumental para fazer os personagens parecerem pequenos diante da vastidão de seus desafios.',
            heading2: 'Além da Decoração',
            para4: 'Esses diretores provam que o cinema não é rádio ilustrado — não é simplesmente diálogo e enredo capturados pela câmera. O meio visual tem seu próprio vocabulário, gramática e poesia.',
            para5: 'Quando descartamos um filme como "todo estilo, sem substância", frequentemente perdemos o ponto. Para esses cineastas, estilo É substância.'
          }
        }
      }
    },
    reviews: {
      description: 'Análises profundas das obras mais cativantes do cinema. Cada crítica desconstrói a experiência, temas e arte que tornam esses filmes dignos do seu tempo.',
      searching: 'Buscando...',
      found: 'Encontradas',
      reviewsCount: 'críticas',
      forQuery: 'para',
      readReview: 'Ler Crítica',
      noImage: 'Sem Imagem',
      noResults: 'Nenhuma crítica corresponde aos seus critérios.',
      clearFilters: 'Limpar filtros',
      director: 'Dir.',
      backToReviews: 'Voltar às Críticas',
      reviewNotFound: 'Crítica Não Encontrada',
      share: 'Compartilhar',
      filmDetails: 'Detalhes do Filme',
      directorLabel: 'Diretor',
      yearLabel: 'Ano',
      runtimeLabel: 'Duração',
      genreLabel: 'Gênero'
    }
  },
  fr: {
    nav: {
      home: 'Accueil',
      reviews: 'Films',
      series: 'Séries TV',
      editorial: 'Éditorial',
      categories: 'Catégories',
      about: 'À propos',
      stars: 'Stars'
    },
    common: {
      viewAll: 'Voir Tout',
      learnMore: 'En Savoir Plus',
      seeMore: 'Voir Plus'
    },
    hero: {
      title: 'Matrix Film Review',
      tagline: 'Au-delà des Notes. Vers le Sens.',
      description: 'Où les films sont décodés, analysés et compris au-delà du divertissement superficiel.'
    },
    sections: {
      featured: 'Critiques en Vedette',
      categories: 'Explorer les Catégories',
      editorial: 'Éditorial',
      signals: 'Signaux Matrix',
      stars: 'Stars d\'Hollywood'
    },
    stars: {
      title: 'Stars d\'Hollywood',
      subtitle: 'Découvrez le talent derrière la magie',
      description: 'Explorez les acteurs, réalisateurs et talents qui donnent vie au cinéma',
      viewAll: 'Voir Toutes les Stars',
      biography: 'Biographie',
      recentMovies: 'Films Récents et Populaires',
      backToStars: 'Retour aux Stars',
      notFound: 'Star Non Trouvée',
      page: 'Page',
      of: 'sur',
      noResults: 'Aucune star trouvée',
      search: 'Rechercher',
      searchPlaceholder: 'Rechercher des acteurs, réalisateurs...',
      searchingFor: 'Résultats pour',
      clearSearch: 'Effacer la recherche'
    },
    series: {
      title: 'Séries TV en Tendance',
      subtitle: 'Shows populaires des plateformes de streaming',
      viewAll: 'Voir Tout',
      pageTitle: 'Séries TV',
      pageDescription: 'Explorez les séries de Netflix, Disney+, HBO et plus',
      searchPlaceholder: 'Rechercher des séries TV...',
      search: 'Rechercher',
      popular: 'Populaire',
      trending: 'Tendances',
      topRated: 'Mieux Notées',
      onTheAir: 'En Diffusion',
      streamingServices: 'Services de Streaming',
      filterByService: 'Filtrer par service de streaming:',
      searchingFor: 'Résultats pour',
      filteringBy: 'Filtrage par',
      clearFilter: 'Effacer le filtre',
      page: 'Page',
      of: 'sur',
      noResults: 'Aucune série trouvée',
      notFound: 'Série Non Trouvée',
      backToSeries: 'Retour aux Séries TV',
      overview: 'Synopsis',
      status: 'Statut',
      availableOn: 'Disponible sur',
      createdBy: 'Créée par',
      cast: 'Distribution',
      seasons: 'Saisons',
      similarShows: 'Séries Similaires'
    },
    categories: {
      newReleases: 'Nouveautés',
      classics: 'Classiques Revisités',
      cult: 'Films Cultes',
      documentaries: 'Documentaires',
      hiddenGems: 'Perles Cachées',
      directors: 'Focus Réalisateurs'
    },
    signals: {
      narrativeDepth: 'Profondeur Narrative',
      emotionalImpact: 'Impact Émotionnel',
      visualIdentity: 'Identité Visuelle',
      originality: 'Originalité',
      rewatchValue: 'Valeur de Revisionnage'
    },
    footer: {
      brand: 'Matrix Film Review',
      tagline: 'Partie de l\'Univers Movie Matrix',
      rights: 'Tous droits réservés',
      links: {
        trailers: 'TheMovieMatrixTrailers',
        tv: 'TheMovieMatrixTV'
      }
    },
    review: {
      theExperience: 'L\'Expérience',
      themesAndMeaning: 'Thèmes et Signification',
      directionAndPerformances: 'Réalisation et Performances',
      visualLanguage: 'Langage Visuel',
      finalThoughts: 'Réflexions Finales',
      similarFilms: 'Films Similaires'
    },
    about: {
      ourMission: 'Notre Mission',
      title: 'À Propos de Matrix Film Review',
      tagline: 'Au-delà des Notes. Vers le Sens.',
      intro1: 'Matrix Film Review est une plateforme de critique cinématographique dédiée à explorer les films au-delà des simples scores. Nous analysons la narration, la réalisation, les thèmes et l\'impact culturel.',
      intro2: 'À une époque de recommandations algorithmiques, nous croyons que quelque chose d\'essentiel se perd. La valeur d\'un film ne peut pas être réduite à un pourcentage. Le cinéma est un art.',
      intro3: 'Notre système Matrix Signals mesure ce qui compte: profondeur narrative, résonance émotionnelle, identité visuelle, originalité et valeur de revisionnage.',
      intro4: 'Cette plateforme fait partie de l\'univers Movie Matrix plus large, connectant bandes-annonces, critiques et recommandations pour les vrais amateurs de cinéma.',
      universeTitle: 'L\'Univers Movie Matrix',
      trailersDesc: 'Bandes-annonces sélectionnées',
      tvDesc: 'Analyses approfondies sur YouTube',
      communityTitle: 'Rejoignez Notre Communauté',
      communityDesc: 'Les fonctionnalités communautaires seront bientôt disponibles.',
      comingSoon: 'Bientôt',
      valuesTitle: 'Ce Que Nous Valorisons',
      ctaTitle: 'Prêt à explorer le cinéma différemment?',
      ctaDesc: 'Découvrez nos critiques approfondies et vivez une analyse cinématographique qui va au-delà de la surface.',
      ctaButton: 'Explorer les Critiques',
      values: {
        deepAnalysis: 'Analyse Profonde',
        deepAnalysisDesc: 'Nous explorons la structure narrative et les techniques qui font résonner les films.',
        visualLanguage: 'Langage Visuel',
        visualLanguageDesc: 'La cinématographie et la composition visuelle sont centrales pour notre compréhension du cinéma.',
        emotionalImpact: 'Impact Émotionnel',
        emotionalImpactDesc: 'Nous mesurons comment les films connectent avec les audiences émotionnellement.',
        culturalContext: 'Contexte Culturel',
        culturalContextDesc: 'Chaque film existe dans un moment culturel. Nous examinons relevance et influence.'
      }
    },
    editorial: {
      description: 'Essais, analyses et explorations des significations profondes du cinéma. Écrits pour ceux qui voient le cinéma comme plus que du divertissement.',
      readArticle: 'Lire l\'Article',
      backToEditorial: 'Retour à l\'Éditorial',
      minRead: 'min de lecture',
      articleNotFound: 'Article Non Trouvé',
      viewAll: 'Voir Tout',
      articles: {
        1: {
          title: 'Films Qui Questionnent la Réalité',
          excerpt: 'De The Matrix à Inception, exploration de l\'obsession du cinéma pour la perception et la vérité.',
          category: 'Analyse',
          content: {
            intro: 'Le cinéma a toujours été un médium qui défie notre perception de la réalité. Des premiers jours du train des frères Lumière arrivant en gare — provoquant la fuite du public terrorisé — aux récits déroutants de la science-fiction moderne, le film a constamment repoussé les limites de ce que nous acceptons comme "réel".',
            para1: 'Les films qui questionnent la réalité de manière la plus efficace ne présentent pas simplement des mondes alternatifs; ils nous forcent à examiner nos hypothèses sur le monde que nous habitons.',
            heading1: 'La Philosophie de la Perception',
            para2: 'Ce qui sépare le cinéma véritablement grand qui questionne la réalité du simple gadget est la profondeur philosophique. Une fin surprise révélant "tout était un rêve" est bon marché; une narration qui nous fait questionner si nos propres expériences pourraient être également illusoires est profonde.',
            para3: 'Ces films touchent à d\'anciennes questions philosophiques — la Caverne de Platon, le Malin Génie de Descartes, le concept bouddhiste de Maya — et les traduisent en expériences viscérales et émotionnellement engageantes.',
            heading2: 'Innovation Technique',
            para4: 'Les meilleurs films de cette tradition ne nous disent pas simplement que la réalité est incertaine; ils nous le montrent à travers une cinématographie innovante, un montage et une conception sonore.',
            para5: 'À une époque où les deepfakes et la réalité virtuelle deviennent de plus en plus sophistiqués, ces films semblent plus pertinents que jamais.'
          }
        },
        2: {
          title: 'Films Qui S\'améliorent Avec le Temps',
          excerpt: 'Pourquoi certains films deviennent plus pertinents et puissants des décennies après leur sortie.',
          category: 'Rétrospective',
          content: {
            intro: 'Certains films arrivent avant leur temps, incompris ou sous-estimés lors de leur sortie, pour être reconnus comme des chefs-d\'œuvre des années ou décennies plus tard.',
            para1: 'Qu\'est-ce qui fait qu\'un film vieillit bien? Ce n\'est pas simplement l\'excellence technique, bien que cela aide. C\'est la capacité de toucher des expériences humaines universelles.',
            heading1: 'L\'Épreuve du Temps',
            para2: 'Considérez Blade Runner, un échec commercial en 1982 qui est maintenant considéré comme l\'un des plus grands films de science-fiction jamais réalisés.',
            para3: 'De même, des films comme Shining, Fight Club et 2001: L\'Odyssée de l\'espace ont été initialement accueillis avec des réactions mitigées.',
            heading2: 'Résonance Culturelle',
            para4: 'Les films qui vieillissent le mieux traitent souvent de thèmes qui transcendent leur contexte immédiat: la nature du pouvoir, la recherche de sens, la complexité des relations humaines.',
            para5: 'À mesure que notre monde change, ces films changent avec nous, révélant de nouvelles couches de sens.'
          }
        },
        3: {
          title: 'Quand le Style Devient l\'Histoire',
          excerpt: 'Réalisateurs qui utilisent l\'esthétique comme arme pour créer du sens au-delà du dialogue.',
          category: 'Essai',
          content: {
            intro: 'Entre les mains de certains réalisateurs, le style visuel transcende la simple décoration pour devenir un élément essentiel de la narration.',
            para1: 'Considérez le travail de Wong Kar-wai, dont les couleurs saturées, le ralenti et les compositions serrées ne sont pas seulement belles — elles expriment les états émotionnels de personnages piégés par le temps, la mémoire et le désir.',
            heading1: 'Langage Visuel',
            para2: 'La symétrie méticuleuse et l\'esthétique de maison de poupée de Wes Anderson ne sont pas des affectations; elles créent des mondes où les personnages existent dans des espaces soigneusement ordonnés.',
            para3: 'Denis Villeneuve utilise l\'espace négatif et l\'échelle monumentale pour faire paraître les personnages petits face à l\'immensité de leurs défis.',
            heading2: 'Au-delà de la Décoration',
            para4: 'Ces réalisateurs prouvent que le cinéma n\'est pas de la radio illustrée. Le médium visuel a son propre vocabulaire, grammaire et poésie.',
            para5: 'Quand nous rejetons un film comme "tout style, pas de substance", nous passons souvent à côté du point. Pour ces cinéastes, le style EST la substance.'
          }
        }
      }
    },
    reviews: {
      description: 'Analyses approfondies des œuvres les plus captivantes du cinéma. Chaque critique déconstruit l\'expérience, les thèmes et l\'art qui rendent ces films dignes de votre temps.',
      searching: 'Recherche...',
      found: 'Trouvées',
      reviewsCount: 'critiques',
      forQuery: 'pour',
      readReview: 'Lire la Critique',
      noImage: 'Pas d\'Image',
      noResults: 'Aucune critique ne correspond à vos critères.',
      clearFilters: 'Effacer les filtres',
      director: 'Réal.',
      backToReviews: 'Retour aux Critiques',
      reviewNotFound: 'Critique Non Trouvée',
      share: 'Partager',
      filmDetails: 'Détails du Film',
      directorLabel: 'Réalisateur',
      yearLabel: 'Année',
      runtimeLabel: 'Durée',
      genreLabel: 'Genre'
    }
  },
  de: {
    nav: {
      home: 'Startseite',
      reviews: 'Filme',
      series: 'TV-Serien',
      editorial: 'Redaktion',
      categories: 'Kategorien',
      about: 'Über uns',
      stars: 'Stars'
    },
    common: {
      viewAll: 'Alle Anzeigen',
      learnMore: 'Mehr Erfahren',
      seeMore: 'Mehr Sehen'
    },
    hero: {
      title: 'Matrix Film Review',
      tagline: 'Jenseits der Bewertungen. In die Bedeutung.',
      description: 'Wo Filme dekodiert, analysiert und über oberflächliche Unterhaltung hinaus verstanden werden.'
    },
    sections: {
      featured: 'Ausgewählte Kritiken',
      categories: 'Kategorien Erkunden',
      editorial: 'Redaktion',
      signals: 'Matrix Signale',
      stars: 'Hollywood-Stars'
    },
    stars: {
      title: 'Hollywood-Stars',
      subtitle: 'Entdecken Sie das Talent hinter der Magie',
      description: 'Erkunden Sie Schauspieler, Regisseure und Talente, die das Kino zum Leben erwecken',
      viewAll: 'Alle Stars Anzeigen',
      biography: 'Biografie',
      recentMovies: 'Aktuelle und Beliebte Filme',
      backToStars: 'Zurück zu Stars',
      notFound: 'Star Nicht Gefunden',
      page: 'Seite',
      of: 'von',
      noResults: 'Keine Stars gefunden',
      search: 'Suchen',
      searchPlaceholder: 'Nach Schauspielern, Regisseuren suchen...',
      searchingFor: 'Ergebnisse für',
      clearSearch: 'Suche löschen'
    },
    series: {
      title: 'Trendende TV-Serien',
      subtitle: 'Beliebte Shows von Streaming-Plattformen',
      viewAll: 'Alle Anzeigen',
      pageTitle: 'TV-Serien',
      pageDescription: 'Entdecken Sie Shows von Netflix, Disney+, HBO und mehr',
      searchPlaceholder: 'TV-Serien suchen...',
      search: 'Suchen',
      popular: 'Beliebt',
      trending: 'Im Trend',
      topRated: 'Bestbewertet',
      onTheAir: 'Aktuell im TV',
      streamingServices: 'Streaming-Dienste',
      filterByService: 'Nach Streaming-Dienst filtern:',
      searchingFor: 'Ergebnisse für',
      filteringBy: 'Gefiltert nach',
      clearFilter: 'Filter löschen',
      page: 'Seite',
      of: 'von',
      noResults: 'Keine Serien gefunden',
      notFound: 'Serie Nicht Gefunden',
      backToSeries: 'Zurück zu TV-Serien',
      overview: 'Übersicht',
      status: 'Status',
      availableOn: 'Verfügbar auf',
      createdBy: 'Erstellt von',
      cast: 'Besetzung',
      seasons: 'Staffeln',
      similarShows: 'Ähnliche Shows'
    },
    categories: {
      newReleases: 'Neuerscheinungen',
      classics: 'Klassiker Neu Betrachtet',
      cult: 'Kultfilme',
      documentaries: 'Dokumentationen',
      hiddenGems: 'Versteckte Schätze',
      directors: 'Regisseur-Spotlights'
    },
    signals: {
      narrativeDepth: 'Erzählerische Tiefe',
      emotionalImpact: 'Emotionale Wirkung',
      visualIdentity: 'Visuelle Identität',
      originality: 'Originalität',
      rewatchValue: 'Wiedersehenswert'
    },
    footer: {
      brand: 'Matrix Film Review',
      tagline: 'Teil des Movie Matrix Universums',
      rights: 'Alle Rechte vorbehalten',
      links: {
        trailers: 'TheMovieMatrixTrailers',
        tv: 'TheMovieMatrixTV'
      }
    },
    review: {
      theExperience: 'Das Erlebnis',
      themesAndMeaning: 'Themen und Bedeutung',
      directionAndPerformances: 'Regie und Darstellungen',
      visualLanguage: 'Visuelle Sprache',
      finalThoughts: 'Abschließende Gedanken',
      similarFilms: 'Ähnliche Filme'
    },
    about: {
      ourMission: 'Unsere Mission',
      title: 'Über Matrix Film Review',
      tagline: 'Jenseits der Bewertungen. In die Bedeutung.',
      intro1: 'Matrix Film Review ist eine Filmkritik-Plattform, die Filme jenseits einfacher Bewertungen erkundet. Wir analysieren Erzählung, Regie, Themen und kulturellen Einfluss.',
      intro2: 'In einer Ära algorithmischer Empfehlungen glauben wir, dass etwas Wesentliches verloren geht. Der Wert eines Films kann nicht auf einen Prozentsatz reduziert werden. Kino ist Kunst.',
      intro3: 'Unser Matrix Signals System misst, was zählt: narrative Tiefe, emotionale Resonanz, visuelle Identität, Originalität und Wiedersehwert.',
      intro4: 'Diese Plattform ist Teil des größeren Movie Matrix Universums, das Trailer, Kritiken und kuratierte Empfehlungen verbindet.',
      universeTitle: 'Das Movie Matrix Universum',
      trailersDesc: 'Kuratierte Trailer aller Genres',
      tvDesc: 'Tiefgehende Analysen auf YouTube',
      communityTitle: 'Tritt Unserer Community Bei',
      communityDesc: 'Community-Funktionen werden bald verfügbar sein.',
      comingSoon: 'Demnächst',
      valuesTitle: 'Was Wir Schätzen',
      ctaTitle: 'Bereit, Kino anders zu erkunden?',
      ctaDesc: 'Entdecke unsere tiefgehenden Kritiken und erlebe Filmanalysen, die über die Oberfläche hinausgehen.',
      ctaButton: 'Kritiken Erkunden',
      values: {
        deepAnalysis: 'Tiefe Analyse',
        deepAnalysisDesc: 'Wir erkunden narrative Struktur und Erzähltechniken, die Filme resonieren lassen.',
        visualLanguage: 'Visuelle Sprache',
        visualLanguageDesc: 'Kinematographie und visuelle Komposition sind zentral für unser Verständnis von Kino.',
        emotionalImpact: 'Emotionale Wirkung',
        emotionalImpactDesc: 'Wir messen, wie Filme emotional mit dem Publikum verbinden.',
        culturalContext: 'Kultureller Kontext',
        culturalContextDesc: 'Jeder Film existiert in einem kulturellen Moment. Wir untersuchen Relevanz und Einfluss.'
      }
    },
    editorial: {
      description: 'Essays, Analysen und Erkundungen der tieferen Bedeutungen des Kinos. Geschrieben für diejenigen, die Film als mehr als Unterhaltung sehen.',
      readArticle: 'Artikel Lesen',
      backToEditorial: 'Zurück zum Editorial',
      minRead: 'Min. Lesezeit',
      articleNotFound: 'Artikel Nicht Gefunden',
      viewAll: 'Alle Anzeigen',
      articles: {
        1: {
          title: 'Filme, Die die Realität Hinterfragen',
          excerpt: 'Von The Matrix bis Inception, Erkundung der Besessenheit des Kinos mit Wahrnehmung und Wahrheit.',
          category: 'Analyse',
          content: {
            intro: 'Das Kino war schon immer ein Medium, das unsere Wahrnehmung der Realität herausfordert. Von den ersten Tagen des Zuges der Brüder Lumière, der in den Bahnhof einfährt — was das Publikum angeblich zur Flucht veranlasste — bis zu den verwirrenden Erzählungen der modernen Science-Fiction hat der Film ständig die Grenzen dessen verschoben, was wir als "real" akzeptieren.',
            para1: 'Die Filme, die die Realität am effektivsten hinterfragen, präsentieren nicht einfach alternative Welten; sie zwingen uns, unsere Annahmen über die Welt, in der wir leben, zu überprüfen.',
            heading1: 'Die Philosophie der Wahrnehmung',
            para2: 'Was wirklich großes Kino, das die Realität hinterfragt, von bloßer Trickserei unterscheidet, ist philosophische Tiefe. Ein Überraschungsende, das enthüllt "alles war nur ein Traum", ist billig; eine Erzählung, die uns fragen lässt, ob unsere eigenen Erfahrungen ebenso illusorisch sein könnten, ist tiefgründig.',
            para3: 'Diese Filme greifen uralte philosophische Fragen auf — Platons Höhle, Descartes\' böser Dämon, das buddhistische Konzept von Maya — und übersetzen sie in viszerale, emotional ansprechende Erfahrungen.',
            heading2: 'Technische Innovation',
            para4: 'Die besten Filme dieser Tradition sagen uns nicht nur, dass die Realität ungewiss ist; sie zeigen es uns durch innovative Kameraarbeit, Schnitt und Sounddesign.',
            para5: 'In einer Ära, in der Deepfakes und virtuelle Realität immer ausgefeilter werden, fühlen sich diese Filme relevanter an als je zuvor.'
          }
        },
        2: {
          title: 'Filme, Die Mit der Zeit Besser Werden',
          excerpt: 'Warum manche Filme Jahrzehnte nach ihrer Veröffentlichung relevanter und kraftvoller werden.',
          category: 'Retrospektive',
          content: {
            intro: 'Manche Filme kommen ihrer Zeit voraus, bei ihrer Veröffentlichung missverstanden oder unterschätzt, nur um Jahre oder Jahrzehnte später als Meisterwerke anerkannt zu werden.',
            para1: 'Was lässt einen Film gut altern? Es ist nicht einfach technische Exzellenz, obwohl das hilft. Es ist die Fähigkeit, universelle menschliche Erfahrungen zu berühren.',
            heading1: 'Die Probe der Zeit',
            para2: 'Betrachten Sie Blade Runner, ein kommerzieller Misserfolg 1982, der heute als einer der größten Science-Fiction-Filme aller Zeiten gilt.',
            para3: 'Ebenso wurden Filme wie Shining, Fight Club und 2001: Odyssee im Weltraum anfangs mit gemischten Reaktionen aufgenommen.',
            heading2: 'Kulturelle Resonanz',
            para4: 'Die Filme, die am besten altern, behandeln oft Themen, die ihren unmittelbaren Kontext überschreiten: die Natur der Macht, die Suche nach Sinn, die Komplexität menschlicher Beziehungen.',
            para5: 'Während sich unsere Welt verändert, verändern sich diese Filme mit uns und enthüllen neue Bedeutungsschichten.'
          }
        },
        3: {
          title: 'Wenn Stil zur Geschichte Wird',
          excerpt: 'Regisseure, die Ästhetik als Waffe nutzen, um Bedeutung jenseits des Dialogs zu schaffen.',
          category: 'Essay',
          content: {
            intro: 'In den Händen bestimmter Regisseure transzendiert visueller Stil die bloße Dekoration und wird zu einem wesentlichen Element des Geschichtenerzählens.',
            para1: 'Betrachten Sie die Arbeit von Wong Kar-wai, dessen gesättigte Farben, Zeitlupen und enge Kompositionen nicht nur schön aussehen — sie drücken die emotionalen Zustände von Charakteren aus, die von Zeit, Erinnerung und Verlangen gefangen sind.',
            heading1: 'Visuelle Sprache',
            para2: 'Wes Andersons akribische Symmetrie und Puppenhausästhetik sind keine Affektiertheit; sie schaffen Welten, in denen Charaktere in sorgfältig geordneten Räumen existieren.',
            para3: 'Denis Villeneuve nutzt negativen Raum und monumentale Dimensionen, um Charaktere klein gegenüber der Weite ihrer Herausforderungen erscheinen zu lassen.',
            heading2: 'Jenseits der Dekoration',
            para4: 'Diese Regisseure beweisen, dass Kino nicht illustriertes Radio ist. Das visuelle Medium hat sein eigenes Vokabular, seine eigene Grammatik und Poesie.',
            para5: 'Wenn wir einen Film als "alles Stil, keine Substanz" abtun, verfehlen wir oft den Punkt. Für diese Filmemacher IST Stil Substanz.'
          }
        }
      }
    },
    reviews: {
      description: 'Tiefgehende Analysen der fesselndsten Werke des Kinos. Jede Kritik dekonstruiert Erfahrung, Themen und Kunstfertigkeit, die diese Filme sehenswert machen.',
      searching: 'Suche...',
      found: 'Gefunden',
      reviewsCount: 'Kritiken',
      forQuery: 'für',
      readReview: 'Kritik Lesen',
      noImage: 'Kein Bild',
      noResults: 'Keine Kritiken entsprechen Ihren Kriterien.',
      clearFilters: 'Filter löschen',
      director: 'Regie',
      backToReviews: 'Zurück zu Kritiken',
      reviewNotFound: 'Kritik Nicht Gefunden',
      share: 'Teilen',
      filmDetails: 'Filmdetails',
      directorLabel: 'Regisseur',
      yearLabel: 'Jahr',
      runtimeLabel: 'Laufzeit',
      genreLabel: 'Genre'
    }
  },
  it: {
    nav: {
      home: 'Home',
      reviews: 'Film',
      series: 'Serie TV',
      editorial: 'Editoriale',
      categories: 'Categorie',
      about: 'Chi siamo',
      stars: 'Stelle'
    },
    common: {
      viewAll: 'Vedi Tutto',
      learnMore: 'Scopri di Più',
      seeMore: 'Vedi Altro'
    },
    hero: {
      title: 'Matrix Film Review',
      tagline: 'Oltre i Voti. Nel Significato.',
      description: 'Dove i film vengono decodificati, analizzati e compresi oltre l\'intrattenimento superficiale.'
    },
    sections: {
      featured: 'Recensioni in Evidenza',
      categories: 'Esplora Categorie',
      editorial: 'Editoriale',
      signals: 'Segnali Matrix',
      stars: 'Stelle di Hollywood'
    },
    stars: {
      title: 'Stelle di Hollywood',
      subtitle: 'Scopri il talento dietro la magia',
      description: 'Esplora gli attori, i registi e i talenti che danno vita al cinema',
      viewAll: 'Vedi Tutte le Stelle',
      biography: 'Biografia',
      recentMovies: 'Film Recenti e Popolari',
      backToStars: 'Torna alle Stelle',
      notFound: 'Stella Non Trovata',
      page: 'Pagina',
      of: 'di',
      noResults: 'Nessuna stella trovata',
      search: 'Cerca',
      searchPlaceholder: 'Cerca attori, registi...',
      searchingFor: 'Risultati per',
      clearSearch: 'Cancella ricerca'
    },
    series: {
      title: 'Serie TV di Tendenza',
      subtitle: 'Show popolari dalle piattaforme di streaming',
      viewAll: 'Vedi Tutto',
      pageTitle: 'Serie TV',
      pageDescription: 'Esplora show da Netflix, Disney+, HBO e altro',
      searchPlaceholder: 'Cerca serie TV...',
      search: 'Cerca',
      popular: 'Popolari',
      trending: 'Di Tendenza',
      topRated: 'Più Votate',
      onTheAir: 'In Onda',
      streamingServices: 'Servizi di Streaming',
      filterByService: 'Filtra per servizio di streaming:',
      searchingFor: 'Risultati per',
      filteringBy: 'Filtrato per',
      clearFilter: 'Cancella filtro',
      page: 'Pagina',
      of: 'di',
      noResults: 'Nessuna serie trovata',
      notFound: 'Serie Non Trovata',
      backToSeries: 'Torna alle Serie TV',
      overview: 'Trama',
      status: 'Stato',
      availableOn: 'Disponibile su',
      createdBy: 'Creata da',
      cast: 'Cast',
      seasons: 'Stagioni',
      similarShows: 'Show Simili'
    },
    categories: {
      newReleases: 'Nuove Uscite',
      classics: 'Classici Rivisitati',
      cult: 'Film Cult',
      documentaries: 'Documentari',
      hiddenGems: 'Gemme Nascoste',
      directors: 'Focus Registi'
    },
    signals: {
      narrativeDepth: 'Profondità Narrativa',
      emotionalImpact: 'Impatto Emotivo',
      visualIdentity: 'Identità Visiva',
      originality: 'Originalità',
      rewatchValue: 'Valore di Revisione'
    },
    footer: {
      brand: 'Matrix Film Review',
      tagline: 'Parte dell\'Universo Movie Matrix',
      rights: 'Tutti i diritti riservati',
      links: {
        trailers: 'TheMovieMatrixTrailers',
        tv: 'TheMovieMatrixTV'
      }
    },
    review: {
      theExperience: 'L\'Esperienza',
      themesAndMeaning: 'Temi e Significato',
      directionAndPerformances: 'Regia e Performance',
      visualLanguage: 'Linguaggio Visivo',
      finalThoughts: 'Considerazioni Finali',
      similarFilms: 'Film Simili'
    },
    about: {
      ourMission: 'La Nostra Missione',
      title: 'Chi Siamo - Matrix Film Review',
      tagline: 'Oltre i Voti. Nel Significato.',
      intro1: 'Matrix Film Review è una piattaforma di critica cinematografica dedicata a esplorare i film oltre i semplici punteggi. Analizziamo narrazione, regia, temi e impatto culturale.',
      intro2: 'In un\'era di raccomandazioni algoritmiche, crediamo che qualcosa di essenziale si perda. Il valore di un film non può essere ridotto a una percentuale. Il cinema è arte.',
      intro3: 'Il nostro sistema Matrix Signals misura ciò che conta: profondità narrativa, risonanza emotiva, identità visiva, originalità e valore di revisione.',
      intro4: 'Questa piattaforma fa parte del più ampio universo Movie Matrix, collegando trailer, recensioni e raccomandazioni curate per i veri amanti del cinema.',
      universeTitle: 'L\'Universo Movie Matrix',
      trailersDesc: 'Trailer selezionati di tutti i generi',
      tvDesc: 'Analisi approfondite su YouTube',
      communityTitle: 'Unisciti alla Nostra Comunità',
      communityDesc: 'Le funzionalità della comunità saranno presto disponibili.',
      comingSoon: 'Prossimamente',
      valuesTitle: 'Cosa Apprezziamo',
      ctaTitle: 'Pronto a esplorare il cinema in modo diverso?',
      ctaDesc: 'Scopri le nostre recensioni approfondite e vivi un\'analisi cinematografica che va oltre la superficie.',
      ctaButton: 'Esplora Recensioni',
      values: {
        deepAnalysis: 'Analisi Profonda',
        deepAnalysisDesc: 'Esploriamo struttura narrativa e tecniche che fanno risuonare i film.',
        visualLanguage: 'Linguaggio Visivo',
        visualLanguageDesc: 'Cinematografia e composizione visiva sono centrali per la nostra comprensione del cinema.',
        emotionalImpact: 'Impatto Emotivo',
        emotionalImpactDesc: 'Misuriamo come i film si connettono emotivamente con il pubblico.',
        culturalContext: 'Contesto Culturale',
        culturalContextDesc: 'Ogni film esiste in un momento culturale. Esaminiamo rilevanza e influenza.'
      }
    },
    editorial: {
      description: 'Saggi, analisi ed esplorazioni dei significati più profondi del cinema. Scritti per chi vede il cinema come qualcosa di più del semplice intrattenimento.',
      readArticle: 'Leggi Articolo',
      backToEditorial: 'Torna all\'Editoriale',
      minRead: 'min di lettura',
      articleNotFound: 'Articolo Non Trovato',
      viewAll: 'Vedi Tutto',
      articles: {
        1: {
          title: 'Film Che Mettono in Discussione la Realtà',
          excerpt: 'Da The Matrix a Inception, esplorando l\'ossessione del cinema per percezione e verità.',
          category: 'Analisi',
          content: {
            intro: 'Il cinema è sempre stato un mezzo che sfida la nostra percezione della realtà. Dai primi giorni del treno dei fratelli Lumière che arrivava alla stazione — causando la fuga terrorizzata del pubblico — alle narrazioni sconcertanti della fantascienza moderna, il film ha costantemente spinto i confini di ciò che accettiamo come "reale".',
            para1: 'I film che mettono in discussione la realtà nel modo più efficace non presentano semplicemente mondi alternativi; ci costringono a esaminare le nostre ipotesi sul mondo che abitiamo.',
            heading1: 'La Filosofia della Percezione',
            para2: 'Ciò che separa il cinema veramente grande che mette in discussione la realtà dal mero trucco è la profondità filosofica. Un finale a sorpresa che rivela "era tutto un sogno" è economico; una narrazione che ci fa dubitare se le nostre stesse esperienze potrebbero essere ugualmente illusorie è profonda.',
            para3: 'Questi film attingono a antiche domande filosofiche — la Caverna di Platone, il Genio Maligno di Cartesio, il concetto buddista di Maya — e le traducono in esperienze viscerali ed emotivamente coinvolgenti.',
            heading2: 'Innovazione Tecnica',
            para4: 'I migliori film di questa tradizione non ci dicono semplicemente che la realtà è incerta; ce lo mostrano attraverso una cinematografia innovativa, montaggio e design del suono.',
            para5: 'In un\'era in cui i deepfake e la realtà virtuale stanno diventando sempre più sofisticati, questi film sembrano più rilevanti che mai.'
          }
        },
        2: {
          title: 'Film Che Migliorano Con il Tempo',
          excerpt: 'Perché alcuni film diventano più rilevanti e potenti decenni dopo la loro uscita.',
          category: 'Retrospettiva',
          content: {
            intro: 'Alcuni film arrivano prima del loro tempo, incompresi o sottovalutati al momento dell\'uscita, solo per essere riconosciuti come capolavori anni o decenni dopo.',
            para1: 'Cosa fa invecchiare bene un film? Non è semplicemente l\'eccellenza tecnica, anche se questo aiuta. È la capacità di toccare esperienze umane universali.',
            heading1: 'La Prova del Tempo',
            para2: 'Considerate Blade Runner, un insuccesso commerciale nel 1982 che ora è considerato uno dei più grandi film di fantascienza mai realizzati.',
            para3: 'Allo stesso modo, film come Shining, Fight Club e 2001: Odissea nello spazio furono inizialmente accolti con reazioni contrastanti.',
            heading2: 'Risonanza Culturale',
            para4: 'I film che invecchiano meglio spesso trattano temi che trascendono il loro contesto immediato: la natura del potere, la ricerca di significato, la complessità delle relazioni umane.',
            para5: 'Man mano che il nostro mondo cambia, questi film cambiano con noi, rivelando nuovi strati di significato.'
          }
        },
        3: {
          title: 'Quando lo Stile Diventa la Storia',
          excerpt: 'Registi che usano l\'estetica come arma per creare significato oltre il dialogo.',
          category: 'Saggio',
          content: {
            intro: 'Nelle mani di certi registi, lo stile visivo trascende la mera decorazione per diventare un elemento essenziale della narrazione.',
            para1: 'Considerate il lavoro di Wong Kar-wai, i cui colori saturi, rallentatori e composizioni strette non sono solo belli — esprimono gli stati emotivi di personaggi intrappolati dal tempo, dalla memoria e dal desiderio.',
            heading1: 'Linguaggio Visivo',
            para2: 'La simmetria meticolosa e l\'estetica da casa delle bambole di Wes Anderson non sono affettazioni; creano mondi dove i personaggi esistono in spazi attentamente ordinati.',
            para3: 'Denis Villeneuve usa lo spazio negativo e la scala monumentale per far sembrare i personaggi piccoli di fronte alla vastità delle loro sfide.',
            heading2: 'Oltre la Decorazione',
            para4: 'Questi registi dimostrano che il cinema non è radio illustrata. Il mezzo visivo ha il proprio vocabolario, grammatica e poesia.',
            para5: 'Quando liquidiamo un film come "tutto stile, niente sostanza", spesso perdiamo il punto. Per questi cineasti, lo stile È sostanza.'
          }
        }
      }
    },
    reviews: {
      description: 'Analisi approfondite delle opere più avvincenti del cinema. Ogni recensione decostruisce l\'esperienza, i temi e l\'arte che rendono questi film degni del tuo tempo.',
      searching: 'Ricerca...',
      found: 'Trovate',
      reviewsCount: 'recensioni',
      forQuery: 'per',
      readReview: 'Leggi Recensione',
      noImage: 'Nessuna Immagine',
      noResults: 'Nessuna recensione corrisponde ai tuoi criteri.',
      clearFilters: 'Cancella filtri',
      director: 'Regia',
      backToReviews: 'Torna alle Recensioni',
      reviewNotFound: 'Recensione Non Trovata',
      share: 'Condividi',
      filmDetails: 'Dettagli Film',
      directorLabel: 'Regista',
      yearLabel: 'Anno',
      runtimeLabel: 'Durata',
      genreLabel: 'Genere'
    }
  }
};

export const mockMovies = [
  {
    id: 1,
    title: 'Blade Runner 2049',
    year: 2017,
    genre: ['Sci-Fi', 'Drama', 'Mystery'],
    director: 'Denis Villeneuve',
    runtime: 164,
    poster: 'https://image.tmdb.org/t/p/w500/gajva2L0rPYkEWjzgFlBXCAVBE5.jpg',
    backdrop: 'https://image.tmdb.org/t/p/original/sAtoMqDVhNDQBc3QJL3RF6hlhGq.jpg',
    excerpt: 'A visual masterpiece that expands upon the original while carving its own philosophical territory.',
    signals: {
      narrativeDepth: 92,
      emotionalImpact: 88,
      visualIdentity: 98,
      originality: 85,
      rewatchValue: 90
    },
    category: 'newReleases'
  },
  {
    id: 2,
    title: 'The Matrix',
    year: 1999,
    genre: ['Sci-Fi', 'Action'],
    director: 'The Wachowskis',
    runtime: 136,
    poster: 'https://image.tmdb.org/t/p/w500/f89U3ADr1oiB1s9GkdPOEpXUk5H.jpg',
    backdrop: 'https://image.tmdb.org/t/p/original/fNG7i7RqMErkcqhohV2a6cV1Ehy.jpg',
    excerpt: 'The film that redefined action cinema and planted seeds of existential doubt in mainstream consciousness.',
    signals: {
      narrativeDepth: 95,
      emotionalImpact: 90,
      visualIdentity: 97,
      originality: 99,
      rewatchValue: 95
    },
    category: 'classics'
  },
  {
    id: 3,
    title: 'Mulholland Drive',
    year: 2001,
    genre: ['Mystery', 'Drama', 'Thriller'],
    director: 'David Lynch',
    runtime: 147,
    poster: 'https://image.tmdb.org/t/p/w500/tVxGt7uffLVhIIcwuldXOMpFBPX.jpg',
    backdrop: 'https://image.tmdb.org/t/p/original/rFiE7TLoGHVB9x8qaLwsrdj4TYN.jpg',
    excerpt: 'A labyrinthine dream that dissolves the boundaries between desire, identity, and Hollywood illusion.',
    signals: {
      narrativeDepth: 96,
      emotionalImpact: 89,
      visualIdentity: 94,
      originality: 98,
      rewatchValue: 92
    },
    category: 'cult'
  },
  {
    id: 4,
    title: 'Parasite',
    year: 2019,
    genre: ['Thriller', 'Drama', 'Comedy'],
    director: 'Bong Joon-ho',
    runtime: 132,
    poster: 'https://image.tmdb.org/t/p/w500/7IiTTgloJzvGI1TAYymCfbfl3vT.jpg',
    backdrop: 'https://image.tmdb.org/t/p/original/TU9NIjwzjoKPwQHoHshkFcQUCG.jpg',
    excerpt: 'A surgical examination of class warfare wrapped in genre-defying brilliance.',
    signals: {
      narrativeDepth: 97,
      emotionalImpact: 94,
      visualIdentity: 93,
      originality: 96,
      rewatchValue: 94
    },
    category: 'newReleases'
  },
  {
    id: 5,
    title: '2001: A Space Odyssey',
    year: 1968,
    genre: ['Sci-Fi', 'Adventure'],
    director: 'Stanley Kubrick',
    runtime: 149,
    poster: 'https://image.tmdb.org/t/p/w500/ve72VxNqjGM69Pk8gWyZFvKqPYl.jpg',
    backdrop: 'https://image.tmdb.org/t/p/original/zmmYdPa8Lxx999Af9vnKiqyXhNh.jpg',
    excerpt: 'Cinema as pure visual symphony, questioning human evolution and artificial consciousness.',
    signals: {
      narrativeDepth: 94,
      emotionalImpact: 80,
      visualIdentity: 99,
      originality: 100,
      rewatchValue: 88
    },
    category: 'classics'
  },
  {
    id: 6,
    title: 'Stalker',
    year: 1979,
    genre: ['Sci-Fi', 'Drama'],
    director: 'Andrei Tarkovsky',
    runtime: 162,
    poster: 'https://image.tmdb.org/t/p/w500/lTqRgRclwJCxiuQkqmqL3HyGmC1.jpg',
    backdrop: 'https://image.tmdb.org/t/p/original/4Hhb2RlJYgv6FwHXcMhb9SLwXni.jpg',
    excerpt: 'A meditative journey into the Zone, where faith, doubt, and desire converge in haunting imagery.',
    signals: {
      narrativeDepth: 99,
      emotionalImpact: 85,
      visualIdentity: 97,
      originality: 96,
      rewatchValue: 82
    },
    category: 'cult'
  },
  {
    id: 7,
    title: 'Arrival',
    year: 2016,
    genre: ['Sci-Fi', 'Drama'],
    director: 'Denis Villeneuve',
    runtime: 116,
    poster: 'https://image.tmdb.org/t/p/w500/x2FJsf1ElAgr63Y3PNPtJrcmpoe.jpg',
    backdrop: 'https://image.tmdb.org/t/p/original/yIZ1xendyqKvY3FGeeUYUd5X9Mm.jpg',
    excerpt: 'Language becomes the key to understanding time, grief, and what it means to truly communicate.',
    signals: {
      narrativeDepth: 95,
      emotionalImpact: 96,
      visualIdentity: 90,
      originality: 94,
      rewatchValue: 93
    },
    category: 'newReleases'
  },
  {
    id: 8,
    title: 'Eraserhead',
    year: 1977,
    genre: ['Horror', 'Fantasy'],
    director: 'David Lynch',
    runtime: 89,
    poster: 'https://image.tmdb.org/t/p/w500/mbvk7HgRh27XM53VwNs1g7FXvbN.jpg',
    backdrop: 'https://image.tmdb.org/t/p/original/c0yXaXgSxp6RcjfuxMBSdpJmjdj.jpg',
    excerpt: 'Industrial nightmare that births cinema\'s most unsettling meditation on fatherhood and anxiety.',
    signals: {
      narrativeDepth: 88,
      emotionalImpact: 78,
      visualIdentity: 99,
      originality: 100,
      rewatchValue: 75
    },
    category: 'cult'
  }
];

export const editorialArticles = [
  {
    id: 1,
    title: 'Films That Question Reality',
    excerpt: 'From The Matrix to Inception, exploring cinema\'s obsession with perception and truth.',
    image: 'https://image.tmdb.org/t/p/original/fNG7i7RqMErkcqhohV2a6cV1Ehy.jpg',
    category: 'Analysis'
  },
  {
    id: 2,
    title: 'Movies That Age Better With Time',
    excerpt: 'Why some films grow more relevant and powerful decades after their release.',
    image: 'https://image.tmdb.org/t/p/original/zmmYdPa8Lxx999Af9vnKiqyXhNh.jpg',
    category: 'Retrospective'
  },
  {
    id: 3,
    title: 'When Style Becomes the Story',
    excerpt: 'Directors who weaponize aesthetics to create meaning beyond dialogue.',
    image: 'https://image.tmdb.org/t/p/original/sAtoMqDVhNDQBc3QJL3RF6hlhGq.jpg',
    category: 'Essay'
  }
];

export const languageOptions = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' }
];
